import cx from "classnames";
import { JSX, ReactNode, useEffect, useId, useRef, useState } from "react";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { QesColors } from "@app/shared/qes-standards/colors";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESRadioButton } from "@app/shared/qes-standards/forms/RadioButton";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";

interface SingleSelectDropdownProps<T> {
  /**
   * Optional class name(s) added to the div outside the dropdown
   */
  containerClassName?: string;
  /**
   * A string to be rendered inside the text input/dropdown button.<br/>
   * This defaults to "x item(s) selected"
   */
  customSelectInputText?: string;
  customWidth?: boolean;
  /**
   * Disables the text field, dropdown button, and all inputs and radios inside the dropdown
   */
  disabled?: boolean;
  /**
   * Visually hides the label above the dropdown
   */
  hideLabel?: boolean;
  /**
   * Hides the search field within the dropdown
   */
  hideSearch?: boolean;
  /**
   * The list of items rendered within the dropdown
   */
  items: Array<DropdownItem<T>>;
  /**
   * The label of the text element outside the dropdown
   */
  label: string | JSX.Element;
  /**
   * A function called on every radio change, returning an array of the items currently selected
   */
  onSelectionChanged?: (tempSelectedItem: DropdownItem<T>) => void;
  /**
   * An array of the items currently selected, to be maintained by a parent component
   */
  selectedItem: DropdownItem<T>;
  /**
   * An optional string that appears in a tooltip to the right of the label.<br/>
   * This is intended to display validation requirements to the user.
   */
  validationTooltipContent?: string | JSX.Element;
  /**
   * Required, static, unique string to help QA Automation run tests involving this component
   */
  testId: string;
  required?: boolean;
  isValid?: boolean;
  /**
   * The alias to refer to the selected items. This will override the default value of "item(s) selected", provide appropriate plural extension for overridden alias.  
   */
  itemAlias?: string;
  /**
   * Display label text in tooltip if text overflows container.
   */
  showTooltipOnOverflow?: boolean;
}

/**
 * A button that summons a dropdown. The dropdown may include
 * a search field for its contents and a line item 
 * for each item passed in through the items prop. A change event handler is called on 
 * every radio click, so there's no need for a submit button. <br /><br />
 * Storybook note: The dotted rectangle is the example's document body, or the area 
 * a user can click in to hide the dropdown.
 */
const SingleSelectDropdown = <T,>(props: SingleSelectDropdownProps<T> & { children?: ReactNode }): JSX.Element => {
  const [isVisible, setIsVisible] = useState(false);
  const [tempSelectedItem, setTempSelectedItem] = useState(props.selectedItem);
  const [selectInputText, setSelectInputText] = useState("")
  const [searchText, setSearchText] = useState("");
  const [visibleItems, setVisibleItems] = useState(props.items);
  const inputRef = useRef<HTMLInputElement>(null);
  const uniqueId = useId();

  useEffect(() => {
    setSearchText("");
  }, [isVisible]);

  useEffect(() => {
    setTempSelectedItem(props.selectedItem);
  }, [isVisible, props.selectedItem]);

  useEffect(() => {
    if (!props.customSelectInputText) {
      if (!!tempSelectedItem) {
        setSelectInputText(`${tempSelectedItem.display || tempSelectedItem.name}`);
      } else {
        setSelectInputText("All");
      }
    }
  }, [tempSelectedItem, visibleItems, props.items]);

  useEffect(() => {
    setVisibleItems(props.items.filter(x => x.name.toLowerCase().includes(searchText.toLowerCase())));
  }, [props.items, searchText]);

  useEffect(() => {
    if (isVisible) {
      const handleExternalClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (target.closest(`#${CSS.escape(uniqueId)}.multiselect-dropdown-container`) === null && isVisible) {
          setIsVisible(false);
          document.body.removeEventListener("click", handleExternalClick);
        }
      };
      document.body.addEventListener("click", handleExternalClick);

      return () => {
        document.body.removeEventListener("click", handleExternalClick);
      };
    }
  }, [isVisible]);

  const updateTempSelectedItem = (key: T) => {
    const newSelectedItem = props.items.find((i) => i.key === key);
    setTempSelectedItem(newSelectedItem);

    if (props.onSelectionChanged)
      props.onSelectionChanged(newSelectedItem);
  };

  return (
    <div className={cx({
      [props.containerClassName]: !!props.containerClassName,
      "multiselect-dropdown-container": true,
    })}
      id={uniqueId}
    >
      <QESTextInput
        containerClassName="select-input"
        disabled={props.disabled}
        hideLabel={props.hideLabel}
        qesRef={inputRef}
        required={props.required}
        isValid={props.isValid}
        inputAppend={
          <QESButton
            qesType="secondary"
            disabled={props.disabled}
            qesTestId={`single-select-dropdown__toggle-button--${props.testId}`}
            onMouseDown={(e) => { e.preventDefault(); inputRef.current.focus(); inputRef.current.click(); }}
            className="select-button"
            aria-label="Toggle Dropdown"
          >
            <div className="singleselect-dropdown-icon"><QesIcon iconName={QesIcons.ChevronDown} description="Expand" width="16" color={QesColors.QesPrimaryGray8} /></div>
          </QESButton>
        }

        label={props.label}
        onClick={() => setIsVisible(!isVisible)}
        readOnly={true}
        testId={`search-radio-dropdown__${props.testId}-select-input`}
        validationTooltipContent={props.validationTooltipContent}
        value={!!props.customSelectInputText ? props.customSelectInputText : selectInputText}
      />
      {isVisible && (
        <div
          className={cx({
            "checkbox-dropdown": true,
            "w-100": !props.customWidth,
          })}
        >
          {!props.hideSearch ?
            <QESTextInput
              testId={`search-radio-dropdown__${props.testId}-search-input`}
              disabled={props.disabled}
              hideLabel={true}
              label=""
              placeholder="Search..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              containerClassName="checkbox-dropdown__label"
            /> : null
          }
          {visibleItems.map((item, index) => {
            if (!item) {
              return;
            }

            const isSelected = tempSelectedItem.key === item.key;

            return (
              <div key={index}>
                <QESRadioButton
                  radioContainerClassName="checkbox-dropdown__label"
                  label={item.name}
                  checked={isSelected}
                  disabled={props.disabled}
                  onChange={() => updateTempSelectedItem(item.key)}
                  testId={`search-radio-dropdown__${props.testId}-radio-${index}`}
                  showTooltipOnOverflow={props.showTooltipOnOverflow}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SingleSelectDropdown;
